//
// # helpContent.ts
//
// Structured help content derived from documentation markdown files.
// Organized by category with expandable sections.
//

import { Book, Keyboard, Lightbulb, Bot, type LucideIcon } from "lucide-react";

export interface HelpSection {
  title: string;
  content: string;
}

export interface HelpTopic {
  title: string;
  description: string;
  keywords: string[];
  sections: HelpSection[];
}

export interface HelpCategory {
  category: string;
  icon: LucideIcon;
  items: HelpTopic[];
}

export const HELP_CONTENT: HelpCategory[] = [
  {
    category: "Design Types",
    icon: Book,
    items: [
      {
        title: "Schematics",
        description: "Logical connectivity and circuit design",
        keywords: [
          "schematic",
          "wire",
          "instance",
          "net",
          "template",
          "circuit",
        ],
        sections: [
          {
            title: "Overview",
            content: `Schematics capture the logical connectivity of your circuit. In GenAlpha, schematics consist of symbol instances connected by wires. Schematics serve as the design intent that layouts implement.

Key Concepts:
• Instance - A placed symbol representing a sub-circuit or device
• Wire - A logical connection between instance terminals
• Pin - An I/O point on the schematic boundary
• Net - A named electrical node (all connected wires share a net)
• Template - A pre-built schematic circuit pattern`,
          },
          {
            title: "Add an Instance",
            content: `1. Right-click on empty canvas space
2. Select "Add Instance" from context menu
3. Choose symbol from available designs
4. Configure instance name and parameters
5. Click to place

Via AI: "Add an instance of inverter symbol"`,
          },
          {
            title: "Draw a Wire",
            content: `1. Press W to enter wire drawing mode
2. Click on an instance terminal to start
3. Click to add bend points (Manhattan routing)
4. Click on destination terminal to finish
5. Escape to cancel

Keyboard shortcut: W`,
          },
          {
            title: "Add a Pin",
            content: `1. Press P to add pin mode
2. Click on schematic boundary
3. Enter pin name and direction (input/output/inout)
4. Connect to internal nets with wires

Keyboard shortcut: P`,
          },
          {
            title: "Clean Up Wires",
            content: `1. Select wires to clean up (or select all)
2. Press Shift+C to straighten and optimize routing

Keyboard shortcut: Shift+C`,
          },
          {
            title: "Load a Template",
            content: `1. Open Command Palette (Ctrl+P)
2. Search for "Load Template"
3. Browse available circuit templates
4. Select template to insert

Via AI: "Load the differential pair template"`,
          },
          {
            title: "Navigate to Layout",
            content: `1. With schematic open, use Command Palette
2. Search for "Navigate to Layout"
3. If multiple layouts linked, choose from picker

Keyboard shortcut: Ctrl+Shift+L (create layout from schematic)`,
          },
        ],
      },
      {
        title: "Layouts",
        description: "Physical implementation with metal routing",
        keywords: [
          "layout",
          "route",
          "metal",
          "via",
          "placement",
          "grid",
          "pdk",
        ],
        sections: [
          {
            title: "Overview",
            content: `Layouts are the physical implementation of your circuit design. In GenAlpha, layouts consist of placed instances (transistors, resistors, etc.) connected by metal routes. All placement and routing follows PDK-defined grids.

Key Concepts:
• Instance - A placed device from the PDK (transistor, resistor, capacitor, etc.)
• Route - A metal path connecting instance terminals
• Pin - An I/O point on the layout boundary for external connections
• Metal Stack - The set of metal layers available for routing
• Via - Automatic connection between adjacent metal layers`,
          },
          {
            title: "Add an Instance",
            content: `1. Right-click on empty canvas space
2. Select "Add Instance" from context menu
3. Choose device type from PDK catalog
4. Configure parameters (width, length, fingers, etc.)
5. Click to place (snaps to grid)

Via AI: "Add a PMOS transistor with W=1u L=100n"`,
          },
          {
            title: "Move an Instance",
            content: `1. Select the instance by clicking
2. Press G to enter grab/move mode
3. Drag to new position (snaps to grid)
4. Click to place, or Escape to cancel

Keyboard shortcut: G (grab)`,
          },
          {
            title: "Transform an Instance",
            content: `With instance selected in move mode:
• R - Rotate 90 degrees
• H - Flip horizontally
• V - Flip vertically`,
          },
          {
            title: "Draw a Route",
            content: `1. Press Alt+R to enter route drawing mode
2. Click to place route points (snaps to routing grid)
3. Use U/D to change metal layers (via auto-generated)
4. Double-click or Enter to finish
5. Escape to cancel

Keyboard shortcut: Alt+R`,
          },
          {
            title: "Add a Pin",
            content: `1. Press P to add pin mode
2. Click on layout boundary location
3. Enter pin name and direction (input/output/inout)
4. Pin snaps to routing grid

Keyboard shortcut: P`,
          },
          {
            title: "View Layer Information",
            content: `Press L to toggle the layer info panel showing:
• Available metal layers
• Current active layer
• Layer colors and properties

Keyboard shortcut: L`,
          },
        ],
      },
      {
        title: "Symbols",
        description: "Reusable design components",
        keywords: ["symbol", "pin", "graphics", "label", "reusable"],
        sections: [
          {
            title: "Overview",
            content: `Symbols define the visual representation and interface of sub-circuits. They appear as instances in schematics and can be linked to schematic implementations.

Key Concepts:
• Pin - An I/O connection point on the symbol boundary
• Graphics - Visual elements like rectangles, lines, text
• Expression Label - Dynamic text showing parameter values
• Selection Box - The clickable boundary of the symbol`,
          },
          {
            title: "Generate Symbol from Schematic",
            content: `1. Open the schematic you want to create a symbol for
2. Open Command Palette (Ctrl+P)
3. Search for "Generate Symbol from Schematic"
4. Symbol is created with pins matching schematic I/O

Via AI: "Generate a symbol from this schematic"`,
          },
          {
            title: "Add a Pin Manually",
            content: `1. Press P to enter pin mode
2. Click on symbol boundary
3. Enter pin name and direction
4. Position determines where wires connect

Keyboard shortcut: P`,
          },
          {
            title: "Add Graphics",
            content: `1. Right-click on canvas
2. Select "Add Shape" from context menu
3. Choose shape type (rectangle, line, etc.)
4. Draw the shape on canvas`,
          },
          {
            title: "Edit Pin Properties",
            content: `1. Right-click a pin
2. Select "Edit Pin..." from the context menu
3. Modify name, direction, or position
4. Confirm to apply`,
          },
        ],
      },
    ],
  },
  {
    category: "Keyboard Shortcuts",
    icon: Keyboard,
    items: [
      {
        title: "View Controls",
        description: "Zoom, pan, and display options",
        keywords: ["zoom", "pan", "grid", "theme", "view", "fit"],
        sections: [
          {
            title: "Shortcuts",
            content: `F          Zoom to fit
Z          Zoom to box (draw rectangle)
Shift+G    Toggle grid display
T          Toggle theme (Auto/Light/Dark)
Shift+T    Toggle trackpad mode
Shift+O    Toggle instance outlines
Shift+R    Redraw canvas
7          Toggle pin labels`,
          },
        ],
      },
      {
        title: "Selection & Editing",
        description: "Select, move, and modify objects",
        keywords: ["select", "click", "box", "delete", "undo", "redo"],
        sections: [
          {
            title: "Shortcuts",
            content: `Click           Select item
Shift+Click     Add/remove from selection
B               Box select
Shift+B         Additive box select
Escape          Deselect all / Cancel
Backspace       Delete selected
Delete          Delete selected
Ctrl+Z          Undo
Ctrl+Shift+Z    Redo`,
          },
        ],
      },
      {
        title: "Instance Manipulation",
        description: "Grab, rotate, and flip objects",
        keywords: ["grab", "move", "rotate", "flip", "transform"],
        sections: [
          {
            title: "Shortcuts",
            content: `G    Grab/move selection
R    Rotate 90 degrees (while moving)
H    Flip horizontal (while moving)
V    Flip vertical (while moving)`,
          },
        ],
      },
      {
        title: "Design Operations",
        description: "Drawing and saving",
        keywords: ["pin", "wire", "route", "save", "draw"],
        sections: [
          {
            title: "Shortcuts",
            content: `P         Add pin (schematic/symbol/layout)
W         Draw wire (schematic)
Alt+R     Draw route (layout)
Shift+C   Wire cleanup (schematic)
Ctrl+S    Save current design`,
          },
        ],
      },
      {
        title: "Layer & Routing",
        description: "Metal layer controls",
        keywords: ["layer", "metal", "routing", "up", "down"],
        sections: [
          {
            title: "Shortcuts",
            content: `L    Toggle layer info panel (layout)
U    Layer up (during routing)
D    Layer down (during routing)`,
          },
        ],
      },
      {
        title: "Panels & Information",
        description: "Open panels and get help",
        keywords: ["panel", "counter", "help", "command"],
        sections: [
          {
            title: "Shortcuts",
            content: `?         Keyboard help overlay
Ctrl+P    Command palette`,
          },
        ],
      },
      {
        title: "Mouse Controls",
        description: "Mouse and trackpad actions",
        keywords: ["mouse", "trackpad", "scroll", "drag", "pan"],
        sections: [
          {
            title: "Actions",
            content: `Pan                  Middle mouse button drag
Zoom                 Scroll wheel
Zoom (trackpad)      Pinch (when trackpad mode on)
Context menu         Right-click
Drag zoom            Ctrl + Middle mouse drag`,
          },
        ],
      },
    ],
  },
  {
    category: "Features",
    icon: Lightbulb,
    items: [
      {
        title: "LVS Verification",
        description: "Compare layout against schematic",
        keywords: [
          "lvs",
          "verify",
          "check",
          "netlist",
          "open",
          "short",
          "mismatch",
        ],
        sections: [
          {
            title: "Overview",
            content: `LVS (Layout vs Schematic) verification checks that a layout correctly implements its corresponding schematic. It extracts the netlist from the layout geometry and compares it against the schematic netlist.

Key Concepts:
• Extracted Netlist - Connectivity derived from layout metal and via geometry
• Schematic Netlist - The intended connectivity from the schematic
• Open - A connection exists in schematic but is missing in layout
• Short - Two nets that should be separate are connected in layout
• Instance Mismatch - A device exists in one netlist but not the other`,
          },
          {
            title: "Run LVS",
            content: `1. Open the layout you want to verify
2. Ensure the layout is linked to a schematic (see Design Linkages)
3. Open Command Palette (Ctrl+P)
4. Search for "Run LVS" and execute
5. Results appear in the LVS Panel

Via AI: "Run LVS on this layout" or "Check if my layout matches the schematic"`,
          },
          {
            title: "Interpret Results",
            content: `The LVS Panel shows:
• Pass/Fail status - Overall result
• Error list - Each mismatch with type and location
• Navigation - Click an error to highlight it in the layout

Common errors:
• Open circuit - Missing route between instances
• Short circuit - Unintended metal overlap
• Missing instance - Device in schematic not placed in layout
• Extra instance - Device in layout not in schematic`,
          },
          {
            title: "Matching Modes",
            content: `Name-based matching (default, faster):
• Matches instances and nets by name
• Requires consistent naming between schematic and layout
• Best for designs created with "Place Like Schematic"

Topology-based matching (structural):
• Matches by circuit structure, ignoring names
• Works when schematic and layout were created independently
• Slower but more flexible`,
          },
        ],
      },
      {
        title: "Symmetry",
        description: "Symmetric placement and routing for matched devices",
        keywords: [
          "symmetry",
          "symmetric",
          "differential",
          "pair",
          "match",
          "axis",
        ],
        sections: [
          {
            title: "Overview",
            content: `Symmetry features help create matched analog circuits with symmetric placement and routing. This is essential for differential pairs, current mirrors, and other matched structures.

Key Concepts:
• Symmetric Group - A set of instances that should be placed as mirror images
• Symmetry Axis - The line about which devices are mirrored
• Matched Devices - Devices intended to have identical characteristics
• Differential Pair - Two matched transistors with complementary signals`,
          },
          {
            title: "Detect Symmetry",
            content: `1. Open a schematic with differential or matched structures
2. Open Command Palette (Ctrl+P)
3. Search for "Detect Symmetry"
4. AI analyzes topology and identifies symmetric groups

Via AI: "Detect symmetry in this schematic"`,
          },
          {
            title: "Place with Symmetry",
            content: `1. After symmetry detection, open or create the layout
2. Use "Place Like Schematic" with symmetry constraints
3. Devices are placed as mirror images about the axis
4. Adjust axis position as needed

Via AI: "Place the differential pair symmetrically"`,
          },
          {
            title: "Route with Symmetry",
            content: `1. After symmetric placement, select nets to route
2. Routing automatically follows symmetric paths
3. Both halves receive identical routing length/structure

Via AI: "Route with symmetric constraints"`,
          },
        ],
      },
      {
        title: "Layout: Generate From Source",
        description: "Generate layout placement from schematic topology",
        keywords: [
          "place",
          "topology",
          "generate",
          "auto",
          "placement",
          "from source",
          "source",
        ],
        sections: [
          {
            title: "Overview",
            content: `Layout: Generate From Source uses your schematic (the "source") to give you a starting point in layout. It finds each instance in the schematic, looks up its corresponding layout, and places those layouts in the target layout in roughly the same relative positions as the schematic.

This gets you started so you don't have to hunt down every device in the depot yourself. It does NOT route the design or produce a finished layout — placement is a starting point for manual refinement.

Benefits:
• Consistent naming between schematic and layout
• Preserves logical groupings
• Starting point for manual refinement`,
          },
          {
            title: "Layout: Generate From Source",
            content: `1. Open the schematic to use as the source
2. Press Ctrl+Shift+L or use Command Palette
3. Select target PDK if prompted
4. Layout is generated with instances placed
5. Refine placement manually as needed

Via AI: "Generate layout from source" or "Generate placement from schematic"

Keyboard shortcut: Ctrl+Shift+L`,
          },
          {
            title: "Refine Placement",
            content: `After auto-generation:
1. Review overall placement
2. Select and move instances with G
3. Rotate (R) and flip (H/V) as needed
4. Group related devices together
5. Run LVS to verify matching`,
          },
        ],
      },
      {
        title: "Common Centroid",
        description: "Interdigitated placement for process gradient immunity",
        keywords: ["centroid", "interdigitate", "abba", "gradient", "matching"],
        sections: [
          {
            title: "Overview",
            content: `Common centroid placement minimizes process gradient effects by arranging matched devices so each group has the same geometric center.

The technique cancels out linear gradients across the chip, improving matching between critical device pairs.

Patterns:
• ABBA - Simple 2-device interleaving
• ABBAABBA - Extended interleaving
• 2D arrays - Grid-based common centroid`,
          },
          {
            title: "Apply Common Centroid",
            content: `1. Select the instances to interdigitate
2. Use Command Palette → "Place Common Centroid"
3. Choose pattern type (ABBA, ABBAABBA, etc.)
4. Devices are arranged with shared geometric center

Via AI: "Place M1 and M2 in common centroid" or "Create ABBA pattern for the current mirror"`,
          },
          {
            title: "Verify Matching",
            content: `After common centroid placement:
1. Check that geometric centers align
2. Verify routing is symmetric
3. Run LVS to confirm connectivity
4. Review parasitics for imbalance`,
          },
        ],
      },
      {
        title: "Parasitics",
        description: "Extract resistance and capacitance from routing",
        keywords: [
          "parasitics",
          "rc",
          "resistance",
          "capacitance",
          "extract",
          "pex",
        ],
        sections: [
          {
            title: "Overview",
            content: `Parasitics extraction (PEX) calculates resistance and capacitance from your layout routing:
• Resistance - Metal wire resistance
• Capacitance - Coupling and substrate capacitance

Use parasitics data to optimize critical signal paths and verify performance.`,
          },
          {
            title: "Enable Parasitics Mode",
            content: `Press Ctrl+Shift+E to toggle parasitics visualization:
• Routes colored by parasitic values
• Hover for detailed RC information
• Critical paths highlighted

Keyboard shortcut: Ctrl+Shift+E`,
          },
          {
            title: "Query Net Parasitics",
            content: `Via AI: "Extract parasitics"
Via AI: "What are the parasitics on net OUT?"
Via AI: "Which net has highest resistance?"`,
          },
        ],
      },
      {
        title: "Design Linkages",
        description: "Connect related designs (schematic ↔ layout ↔ symbol)",
        keywords: [
          "link",
          "linkage",
          "connect",
          "navigate",
          "forward",
          "backward",
        ],
        sections: [
          {
            title: "Overview",
            content: `Design linkages connect related designs so you can navigate between them and run verification.

Linkage Types:
• Schematic ↔ Symbol - The schematic implements the symbol interface
• Layout ↔ Schematic - The layout implements the schematic circuit
• Forward/Backward links - Navigate in either direction`,
          },
          {
            title: "Link Schematic to Symbol",
            content: `1. Open the schematic
2. Use Command Palette (Ctrl+P) → "Link to Symbol"
3. Select the target symbol from picker
4. Linkage is established

Via AI: "Link this schematic to its symbol"`,
          },
          {
            title: "Link Layout to Schematic",
            content: `1. Open the layout
2. Use Command Palette → "Link to Schematic"
3. Select the target schematic
4. Enables LVS verification

Via AI: "Link this layout to its schematic"`,
          },
          {
            title: "Navigate via Linkage",
            content: `1. Right-click on design in explorer
2. Select "Navigate to Linked..."
3. Choose linked design to open
4. Or use Ctrl+Shift+L from schematic to create/open layout`,
          },
        ],
      },
    ],
  },
  {
    category: "AI Assistant",
    icon: Bot,
    items: [
      {
        title: "Overview",
        description: "Natural language design assistance",
        keywords: ["ai", "assistant", "agent", "chat", "help", "question"],
        sections: [
          {
            title: "About the AI Assistant",
            content: `GenAlpha includes an AI assistant that can help with design tasks through natural language interaction. You can ask questions, request operations, and get guidance.

The AI can:
• Answer questions about GenAlpha features
• Make changes to your design
• Analyze your circuit
• Run verification commands
• Place and route components`,
          },
          {
            title: "Ask a Question",
            content: `Simply type your question in the chat:
• "How do I run LVS?"
• "What are the available metal layers?"
• "How do I create a symmetric layout?"

The AI will provide instructions or offer to perform the action.`,
          },
          {
            title: "Request an Action",
            content: `Ask the AI to do something:
• "Place the transistors symmetrically"
• "Run LVS on this layout"
• "Generate a symbol from this schematic"
• "Add a PMOS transistor with W=1u L=100n"

The AI will execute the operation and report results.`,
          },
          {
            title: "Get Design Information",
            content: `Ask about your current design:
• "What nets are open?"
• "List all instances in this layout"
• "What are the parasitics on net OUT?"
• "What topology is this circuit?"`,
          },
        ],
      },
      {
        title: "Schematic Assistance",
        description: "AI help with schematic design",
        keywords: ["schematic", "template", "instance", "convert", "topology"],
        sections: [
          {
            title: "Load Templates",
            content: `Ask the AI to load circuit templates:
• "Load the differential pair template"
• "Show me available templates"
• "Load a current mirror"

Templates provide pre-built circuit patterns.`,
          },
          {
            title: "Add Instances",
            content: `Ask the AI to add components:
• "Add an inverter instance"
• "Place a buffer here"
• "Add a NAND gate from the library"`,
          },
          {
            title: "Analyze Topology",
            content: `Ask the AI about your circuit:
• "What topology is this circuit?"
• "Identify the differential pairs"
• "What kind of amplifier is this?"`,
          },
          {
            title: "Generate Symbol",
            content: `Ask the AI to create symbols:
• "Generate a symbol from this schematic"
• "Create a symbol with these pins"`,
          },
        ],
      },
      {
        title: "Layout Assistance",
        description: "AI help with layout design",
        keywords: ["layout", "place", "route", "guard", "ring", "symmetry"],
        sections: [
          {
            title: "Placement",
            content: `Ask the AI to place instances:
• "Place layout like schematic"
• "Place the differential pair symmetrically"
• "Place M1 and M2 in common centroid"
• "Add a PMOS transistor with W=1u"`,
          },
          {
            title: "Routing",
            content: `Ask the AI to route nets:
• "Route all unconnected nets"
• "Route the output net"
• "Route with symmetric constraints"`,
          },
          {
            title: "Guard Rings",
            content: `Ask the AI to add protection:
• "Add guard ring around the input pair"
• "Create p-tap ring around these NMOS"
• "Add substrate contacts"`,
          },
        ],
      },
      {
        title: "Verification Assistance",
        description: "AI help with LVS and debugging",
        keywords: ["verify", "lvs", "error", "debug", "netlist", "fix"],
        sections: [
          {
            title: "Run Verification",
            content: `Ask the AI to verify your design:
• "Run LVS on this layout"
• "Check if my layout matches the schematic"
• "Extract the netlist"`,
          },
          {
            title: "Interpret Errors",
            content: `Ask the AI to explain errors:
• "What do these LVS errors mean?"
• "Why is there an open on net VDD?"
• "Explain this short circuit"`,
          },
          {
            title: "Get Fix Suggestions",
            content: `Ask the AI for help fixing issues:
• "How do I fix this LVS error?"
• "What's causing the open circuit?"
• "Help me debug this short"`,
          },
        ],
      },
    ],
  },
];
